import { Box, Button, Stack, TextField } from '@mui/material';
import { useState } from 'react';

import { useBoardEditingStore, type WallBoard, type WallCard } from '@walls/store';
import { useCardCommands } from '../cards/actions/useCardCommands';
import { useCardImage } from '../cards/hooks/useCardImage';
import { ImageCardImage } from '../cards/ImageCardImage';
import { CardActionPanel } from './CardActionPanel';
import { CardColorPicker } from './CardColorPicker';
import { useFocusTextFieldAtEnd } from './hooks/useFocusTextFieldAtEnd';
import { imageCardColors } from './utils/cardColors';

interface EditImageCardDialogProps {
  board: WallBoard;
  card: WallCard;
}

export function EditImageCardDialog({ board, card }: EditImageCardDialogProps) {
  const stopEditing = useBoardEditingStore((state) => state.stopEditing);
  const cardCommands = useCardCommands(board);
  const titleField = useFocusTextFieldAtEnd([card.id]);

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [title, setTitle] = useState(card.title ?? '');
  const [color, setColor] = useState(card.color ?? '#ffffff');
  const [isSaving, setIsSaving] = useState(false);
  const imageUrl = useCardImage(board, card);

  const close = () => {
    if (!isSaving) {
      stopEditing();
    }
  };

  const save = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);

    try {
      await cardCommands.updateImageCard(card.id, title.trim(), color);
      stopEditing();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CardActionPanel
      backgroundColor={color}
      onClose={close}
      onClick={() => setShowColorPicker(false)}
    >
      {imageUrl && (
        <Box sx={{ height: 220, position: 'relative', width: '100%' }}>
          <ImageCardImage alt={title || 'Card image'} objectFit="contain" src={imageUrl} />
        </Box>
      )}
      <TextField
        inputRef={titleField.inputRef}
        label="Image title"
        value={title}
        sx={{ backgroundColor: 'rgb(255, 255, 255, 0.5)' }}
        onFocus={titleField.onFocus}
        onChange={(event) => setTitle(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void save();
          }
        }}
      />
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
        <Box
          sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
          onClick={(event) => event.stopPropagation()}
        >
          <Box
            component="button"
            type="button"
            aria-label={`Use color ${color}`}
            onClick={() => setShowColorPicker(!showColorPicker)}
            sx={{
              bgcolor: color,
              border: '1px solid #d0d5dd',
              borderRadius: '50%',
              boxShadow: color === '#ffffff' ? 'inset 0 0 0 1px #e4e7ec' : undefined,
              cursor: 'pointer',
              height: 30,
              width: 30,
              p: 0,
            }}
          />
          {showColorPicker && (
            <CardColorPicker
              colors={imageCardColors}
              value={color}
              onChange={(nextColor) => {
                setShowColorPicker(false);
                setColor(nextColor);
              }}
            />
          )}
        </Box>
        <Button variant="contained" onClick={() => void save()} disabled={isSaving}>
          Done
        </Button>
      </Stack>
    </CardActionPanel>
  );
}
